import { Navigate, useParams } from "react-router";
import { trainers } from "../data/trainers";
import { programs } from "../data/programs";
import {
  trainerSubscribeLinks,
  programLinks,
  hasStripeLink,
} from "../data/stripeLinks";
import DownloadCTASection from "../components/DownloadCTASection";

function parseVimeoVideo(value) {
  if (!value) return null;
  const str = String(value).trim();

  const idMatch = str.match(/(\d+)/);
  if (!idMatch) return null;

  const hParamMatch = str.match(/[?&]h=([a-z0-9]+)/i);
  const pathHashMatch = str.match(/\d+\/([a-z0-9]{8,})/i);
  const hash = hParamMatch?.[1] || pathHashMatch?.[1] || null;

  return { id: idMatch[1], hash };
}

export default function TrainerProfilePage() {
  const { trainerSlug } = useParams();

  const trainer = trainers.find((item) => item.slug === trainerSlug);

  if (!trainer) {
    return <Navigate to="/" replace />;
  }

  const trainerPrograms = programs.filter((program) =>
    program.trainerSlugs?.includes(trainer.slug)
  );

  const vimeoVideo = parseVimeoVideo(trainer.previewVideo);
  const vimeoSrc = vimeoVideo
    ? `https://player.vimeo.com/video/${vimeoVideo.id}?loop=1&title=0&byline=0&portrait=0${
        vimeoVideo.hash ? `&h=${vimeoVideo.hash}` : ""
      }`
    : null;

  const subscribeLink = trainerSubscribeLinks[trainer.slug];

  return (
    <>
      <section className="section trainer-profile">
        <div className="container">
          <div
            className={`trainer-profile__hero${
              vimeoVideo ? " trainer-profile__hero--withVideo" : ""
            }`}
          >
            <div className="trainer-profile__identity">
              <div className="trainer-profile__avatar">
                <img src={trainer.image} alt={trainer.name} />
              </div>

              <div>
                <p className="eyebrow">TRAINER PROFILE</p>
                <h1>{trainer.name}</h1>
                <p className="trainer-profile__role">{trainer.role}</p>
              </div>
            </div>

            {vimeoVideo && (
              <div className="trainer-profile__video">
                <iframe
                  src={vimeoSrc}
                  allow="fullscreen; picture-in-picture"
                  title={`${trainer.name} preview video`}
                />
              </div>
            )}

            <div className="trainer-profile__body">
              <p className="trainer-profile__bio">
                {trainer.longBio || trainer.description}
              </p>

              {trainer.quote && (
                <div className="trainer-profile__quote">
                  <span>“{trainer.quote}”</span>
                </div>
              )}

              <div className="trainer-profile__tags">
                {trainer.specialties.map((item) => (
                  <span key={item} className="tag">
                    {item}
                  </span>
                ))}
              </div>

              <div className="trainer-profile__stats">
                <div className="trainer-card__statBox">
                  <span className="trainer-card__statNumber">
                    {trainer.experience}
                  </span>
                  <span className="trainer-card__statLabel">
                    Years experience
                  </span>
                </div>

                <div className="trainer-card__statBox">
                  <span className="trainer-card__statNumber">
                    {trainer.clients}
                  </span>
                  <span className="trainer-card__statLabel">
                    Clients coached
                  </span>
                </div>

                <div className="trainer-card__statBox">
                  <span className="trainer-card__statNumber">
                    {trainerPrograms.length}
                  </span>
                  <span className="trainer-card__statLabel">
                    Programs in app
                  </span>
                </div>
              </div>

              <div className="trainer-profile__cta">
                {hasStripeLink(subscribeLink) ? (
                  <a
                    className="btn btn--primary trainer-profile__subscribe"
                    href={subscribeLink}
                  >
                    Subscribe to {trainer.name}
                  </a>
                ) : (
                  <button
                    type="button"
                    className="btn btn--primary trainer-profile__subscribe"
                    disabled
                  >
                    Subscribe — coming soon
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section trainer-programs" id="trainer-programs">
        <div className="container">
          <div className="section-heading trainer-programs__heading">
            <p className="eyebrow">PROGRAMS</p>
            <h2>{trainer.name}'s Programs</h2>
            <p className="section-copy">
              All programs from this trainer available in the app.
            </p>
          </div>

          {trainerPrograms.length > 0 ? (
            <div className="trainer-programs__grid">
              {trainerPrograms.map((program) => (
                <article className="program-card" key={program.id}>
                  <div className="program-card__inner">
                    <div className="program-card__media">
                      <img
                        src={program.image}
                        alt={program.title}
                        className="program-card__image"
                      />

                      <div className="program-card__overlay">
                        <span className="program-pill">{program.category}</span>
                        <span className="program-pill">{program.level}</span>
                      </div>
                    </div>

                    <div className="program-card__content">
                      <h3>{program.title}</h3>
                      <p>{program.description}</p>

                      {hasStripeLink(programLinks[program.id]) ? (
                        <a
                          className="btn btn--primary btn--small program-card__cta"
                          href={programLinks[program.id]}
                        >
                          Get program
                        </a>
                      ) : (
                        <button
                          type="button"
                          className="btn btn--primary btn--small program-card__cta"
                          disabled
                        >
                          Coming soon
                        </button>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="trainer-programs__empty">
              <p>Programs from this trainer are coming soon.</p>
            </div>
          )}
        </div>
      </section>

      <DownloadCTASection />
    </>
  );
}
